<template>
  <section class="mb-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h5 class="mb-0">{{ label }}</h5>
      <div>
        <input ref="imageInput" type="file" accept="image/*" multiple class="d-none" @change="addImages" />
        <input ref="replaceInput" type="file" accept="image/*" class="d-none" @change="replaceImage" />
        <b-button size="sm" variant="primary" @click="$refs.imageInput.click()">
          <i class="fa fa-plus"></i> Tambah Gambar
        </b-button>
      </div>
    </div>

    <small v-if="errorMessage" class="text-danger d-block mb-2">{{ errorMessage }}</small>
    <p v-if="images.length === 0" class="text-muted">{{ emptyText }}</p>
    <div v-else class="row">
      <b-col v-for="(item, index) in images" :key="item.id" cols="6" md="4" lg="3" class="mb-3">
        <b-card class="h-100 p-2">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <span class="text-muted">{{ index + 1 }}</span>
            <div>
              <b-button
                size="sm"
                variant="outline-primary"
                class="mr-1"
                :aria-label="`Ganti ${label.toLowerCase()} ${index + 1}`"
                @click="openReplace(item.id)"
              >
                <i class="fa fa-exchange"></i> Ganti
              </b-button>
              <b-button
                size="sm"
                variant="danger"
                :aria-label="`Hapus ${label.toLowerCase()} ${index + 1}`"
                @click="removeImage(item.id)"
              >
                <i class="fa fa-trash"></i> Hapus
              </b-button>
            </div>
          </div>
          <img
            v-if="previews[item.id]"
            :src="previews[item.id]"
            :alt="`Preview ${label.toLowerCase()} ${index + 1}: ${getFileName(item)}`"
            class="img-fluid w-100"
            style="height: 180px; object-fit: contain"
          />
          <small class="text-truncate d-block mt-2" :title="getFileName(item)">{{ getFileName(item) }}</small>
        </b-card>
      </b-col>
    </div>
  </section>
</template>

<script>
export default {
  name: 'InputImageMultiple',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: 'Gambar',
    },
    emptyText: {
      type: String,
      default: 'Belum ada gambar.',
    },
  },
  data() {
    return {
      previews: {},
      previewFiles: {},
      nextImageId: 1,
      replaceTargetId: null,
      errorMessage: '',
    }
  },
  mounted() {
    this.syncPreviews(this.images)
    this.updateNextImageId()
  },
  beforeDestroy() {
    Object.values(this.previews).forEach((url) => URL.revokeObjectURL(url))
  },
  computed: {
    images() {
      return this.value || []
    },
  },
  watch: {
    images: {
      deep: true,
      handler(images) {
        this.syncPreviews(images)
        this.updateNextImageId()
      },
    },
  },
  methods: {
    updateNextImageId() {
      const maxId = this.images.reduce((max, image) => Math.max(max, Number(image.id) || 0), 0)
      this.nextImageId = Math.max(this.nextImageId, maxId + 1)
    },
    syncPreviews(images) {
      const retainedIds = new Set(images.map((image) => String(image.id)))
      Object.keys(this.previews).forEach((id) => {
        if (!retainedIds.has(String(id))) {
          URL.revokeObjectURL(this.previews[id])
          this.$delete(this.previews, id)
          this.$delete(this.previewFiles, id)
        }
      })
      images.forEach((image) => {
        if (image.file) {
          if (this.previewFiles[image.id] !== image.file) {
            if (this.previews[image.id]) URL.revokeObjectURL(this.previews[image.id])
            this.$set(this.previews, image.id, URL.createObjectURL(image.file))
            this.$set(this.previewFiles, image.id, image.file)
          }
        } else if (image.url && this.previews[image.id] !== image.url) {
          this.$set(this.previews, image.id, image.url)
          this.$set(this.previewFiles, image.id, null)
        }
      })
    },
    getFileName(item) {
      return item.file?.name || item.nama_file || 'Gambar'
    },
    addImages(event) {
      const files = Array.from(event.target.files || [])
      const validFiles = files.filter((file) => file.type.startsWith('image/'))
      this.errorMessage = validFiles.length === files.length ? '' : 'Hanya file gambar yang dapat ditambahkan.'

      const additions = validFiles.map((file) => ({
        id: this.nextImageId++,
        serverId: null,
        id_file: null,
        nama_file: '',
        file,
        url: '',
        uploaded: false,
      }))
      if (additions.length > 0) this.$emit('input', [...this.images, ...additions])
      event.target.value = ''
    },
    openReplace(id) {
      this.replaceTargetId = id
      this.$refs.replaceInput.click()
    },
    replaceImage(event) {
      const [file] = Array.from(event.target.files || [])
      event.target.value = ''

      if (!file) return
      if (!file.type.startsWith('image/')) {
        this.errorMessage = 'Hanya file gambar yang dapat diunggah.'
        return
      }

      this.errorMessage = ''
      const items = this.images.map((image) =>
        image.id === this.replaceTargetId ? { ...image, file, uploaded: false } : image,
      )
      this.$emit('input', items)
      this.replaceTargetId = null
    },
    removeImage(id) {
      this.$emit(
        'input',
        this.images.filter((image) => image.id !== id),
      )
    },
  },
}
</script>
